import { PublicClientApplication } from '@azure/msal-browser';

export const msalConfig = {
  auth: {
    clientId: process.env.REACT_APP_AZURE_CLIENT_ID,
    authority: `https://login.microsoftonline.com/${process.env.REACT_APP_AZURE_TENANT_ID}`,
    redirectUri: `${window.location.origin}/auth-redirect`,
    navigateToLoginRequestUrl: false,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
};

export const loginRequest = {
  scopes: ['openid', 'profile', 'email', 'User.Read'],
};

// Requested incrementally by the "Your Day" page, NOT at login. Kept out of
// loginRequest on purpose: adding mailbox scopes there would re-prompt every
// employee for consent on their next sign-in, including everyone who never opens
// the page.
export const graphMailRequest = {
  scopes: ['Mail.Read', 'Calendars.Read'],
};

// Requested by Your Day ONLY when someone actually has shared/delegated
// mailboxes to choose from, and only for reading one of them.
//
// Deliberately a superset of graphMailRequest rather than an addition to it. Two
// reasons, and the second is the important one:
//
//   1. Microsoft issues one token per request, so a shared read needs all four
//      scopes on the same token — asking for the two `.Shared` ones alone would
//      produce a token that can open service@ but not the reader's own inbox.
//   2. Keeping graphMailRequest untouched is what stopped this feature breaking
//      everyone else's briefing. Widening that constant would have made every
//      employee's next silent token acquisition fail interaction_required, so
//      the whole firm would have met a "Connect Outlook" button for a feature
//      most of them will never use. This way the extra prompt only ever reaches
//      people who have a second mailbox — and with tenant-wide admin consent on
//      the Entra app registration, not even them.
//
// `.Shared` covers shared mailboxes AND mailboxes another person has delegated;
// Exchange does not distinguish them at the permission level, and neither does
// this app.
export const graphSharedMailRequest = {
  scopes: ['Mail.Read', 'Calendars.Read', 'Mail.Read.Shared', 'Calendars.Read.Shared'],
};

// Requested by the Calendar page. Kept separate from graphMailRequest so someone
// who only uses Your Day is never asked to consent to calendar WRITE access —
// and separate from loginRequest so nobody is asked at sign-in at all.
export const graphCalendarRequest = {
  scopes: ['Calendars.ReadWrite', 'Calendars.ReadWrite.Shared'],
};

// Requested by Your Day only when someone actually files a message into a folder.
// Its own request, and NOT part of graphMailRequest, because it is the only mail
// scope that can change a mailbox: reading the briefing must never ask for write
// access, so the prompt appears on first use of the Move control and only for the
// people who use it. Graph has no move-only mail permission — Mail.ReadWrite is
// the least-privileged scope that permits POST /messages/{id}/move at all.
export const graphMailWriteRequest = {
  scopes: ['Mail.ReadWrite'],
};

// The same, for filing a message in a SHARED mailbox. Its own request for the
// same reason graphSharedMailRequest is separate from graphMailRequest: someone
// who only files their own mail should never be asked to consent to writing
// anyone else's. Requested on first use of the Move control while a shared
// mailbox is selected, and never otherwise.
export const graphSharedMailWriteRequest = {
  scopes: ['Mail.ReadWrite', 'Mail.ReadWrite.Shared'],
};

// Requested by the Planner page. Its own scope set for the same reason as the
// two above: someone who never opens Planner is never asked to consent to their
// task boards, and Tasks.Read grants nothing over mail or calendar.
export const graphTasksRequest = {
  scopes: ['Tasks.Read'],
};

// Requested by the Events page, which reads the firm's events calendar out of a
// shared mailbox. Read-only and calendar-only: `.Shared` is what reaches another
// mailbox's calendar, and nothing here touches mail.
export const graphEventsRequest = {
  scopes: ['Calendars.Read', 'Calendars.Read.Shared'],
};

// Requested by the Tasks page, which WRITES: ticking a task off and assigning one
// to a colleague. Kept separate from graphTasksRequest so the read-only Planner
// summary never asks for write access — someone who only wants to see their
// boards should not have to consent to something that can change them.
//
// User.ReadBasic.All is here because a Planner assignment is keyed by the
// assignee's Entra object id, not their email, so the picker has to resolve one
// before it can write. It reads name and id for people in the directory and
// nothing else. The app-only credential could do this lookup with no extra
// consent, but it holds Authentication Administrator and app/graph.py is
// deliberately built to keep it away from per-page code.
export const graphTasksWriteRequest = {
  scopes: ['Tasks.ReadWrite', 'User.ReadBasic.All'],
};

// Breadcrumb for the mobile consent path. acquireTokenRedirect leaves the app
// and comes back through /auth-redirect, which otherwise assumes every redirect
// is a fresh login and would drop the user on the dashboard. AuthRedirect reads
// this to send them back where they were instead.
const RETURN_TO_KEY = 'msalReturnTo';

export function setMsalReturnTo(path) {
  try { sessionStorage.setItem(RETURN_TO_KEY, path); } catch (e) { /* private mode */ }
}

export function takeMsalReturnTo() {
  try {
    const v = sessionStorage.getItem(RETURN_TO_KEY);
    sessionStorage.removeItem(RETURN_TO_KEY);
    return v;
  } catch (e) {
    return null;
  }
}

export const msalInstance = new PublicClientApplication(msalConfig);

export let msalRedirectResult = undefined;
export function setMsalRedirectResult(r) { msalRedirectResult = r; }
